<template>
  <div class="w-full space-y-4 text-slate-100">
    <!-- TOP BAR -->
    <div
      class="min-w-0 border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg"
      style="border-radius: 26px;"
    >
      <div class="flex flex-col gap-4 px-4 py-4 sm:px-5 sm:py-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <!-- LEFT -->
          <div class="min-w-0">
            <nav class="hidden sm:flex" aria-label="Breadcrumb">
              <ol role="list" class="flex items-center gap-2 text-xs text-slate-400">
                <li class="flex items-center">
                  <router-link
                    to="/hub"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-70 text-slate-200 transition hover:bg-slate-600"
                  >
                    <HomeIcon class="h-4 w-4" aria-hidden="true" />
                    <span class="sr-only">Home</span>
                  </router-link>
                </li>

                <li class="flex items-center gap-2">
                  <svg
                    class="h-4 w-4 text-slate-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  <span class="font-medium text-slate-300">Trasy</span>
                </li>
              </ol>
            </nav>

            <div class="flex items-center sm:hidden">
              <router-link
                to="/hub"
                class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-700 px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-600"
              >
                &larr; Cofnij
              </router-link>
            </div>

            <div class="mt-3">
              <div class="text-lg font-semibold tracking-tight text-white">
                Zarządzanie trasami
              </div>
              <div class="mt-1 text-xs text-slate-400">
                Lista tras bieżących i archiwalnych wraz z pojemnością, regionami i statusem.
              </div>
            </div>
          </div>

          <!-- RIGHT -->
          <div class="flex shrink-0 items-center gap-2">
            <button
              type="button"
              @click.prevent="visit('/dashboard/routes/regions')"
              class="inline-flex items-center gap-2 rounded-2xl border border-slate-600 bg-slate-700 px-4 py-2.5 text-xs font-semibold text-slate-100 shadow-sm transition hover:bg-slate-600"
            >
              Zarządzaj regionami
            </button>

            <button
              type="button"
              @click.prevent="visit('/dashboard/routes/create')"
              class="inline-flex items-center gap-2 rounded-2xl border border-slate-600 bg-slate-700 px-4 py-2.5 text-xs font-semibold text-slate-100 shadow-sm transition hover:bg-slate-600"
            >
              Dodaj trasę
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLE CARD -->
    <div
      class="border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg"
      style="border-radius: 26px;"
    >
      <div class="border-b border-slate-700 px-4 py-4 sm:px-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div class="min-w-0">
            <div class="text-sm font-semibold text-white">
              {{ showArchive ? 'Archiwum tras' : 'Trasy bieżące' }}
            </div>
            <div class="mt-1 text-xs text-slate-400">
              Łącznie pozycji: {{ routesList.length }}
            </div>
          </div>

          <!-- SWITCH -->
          <div class="shrink-0">
            <nav
              class="inline-flex rounded-2xl border border-slate-700 bg-slate-900 bg-opacity-50 p-1 shadow-inner"
              aria-label="Tabs"
            >
              <button
                type="button"
                @click.prevent="changeList('current')"
                :class="[
                  !showArchive
                    ? 'bg-slate-700 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800',
                  'rounded-xl px-4 py-2 text-sm font-semibold transition'
                ]"
              >
                Trasy bieżące
              </button>

              <button
                type="button"
                @click.prevent="changeList('archive')"
                :class="[
                  showArchive
                    ? 'bg-slate-700 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800',
                  'rounded-xl px-4 py-2 text-sm font-semibold transition'
                ]"
              >
                Archiwum tras
              </button>
            </nav>
          </div>
        </div>
      </div>

      <div class="px-4 py-4 sm:px-5">
        <div v-if="!queryLoading" class="overflow-hidden rounded-2xl border border-slate-700">
          <div class="w-full min-w-0 overflow-x-auto">
            <table class="w-full divide-y divide-slate-700 routesTable" style="table-layout: fixed;">
              <thead class="bg-slate-700 bg-opacity-70">
                <tr>
                  <!-- ID -->
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold whitespace-nowrap text-slate-100 sm:pl-6"
                    style="width: 90px;"
                  >
                    <div class="flex items-center gap-1">
                      ID
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === '_id' && sort.type === 'asc'"
                        @click.prevent="setSort('_id', 'desc')"
                        :class="iconCls('_id')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('_id', 'asc')"
                        :class="iconCls('_id')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- Nazwa -->
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100"
                    style="width: 180px;"
                  >
                    <div class="flex items-center gap-1">
                      Nazwa trasy
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'name' && sort.type === 'asc'"
                        @click.prevent="setSort('name', 'desc')"
                        :class="iconCls('name')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('name', 'asc')"
                        :class="iconCls('name')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- Status -->
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100"
                    style="width: 150px;"
                  >
                    <div class="flex items-center gap-1">
                      Status
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'status' && sort.type === 'asc'"
                        @click.prevent="setSort('status', 'desc')"
                        :class="iconCls('status')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('status', 'asc')"
                        :class="iconCls('status')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- Region -->
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100"
                    style="width: 180px;"
                  >
                    <div class="flex items-center gap-1">
                      Region
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'mainRegionName' && sort.type === 'asc'"
                        @click.prevent="setSort('mainRegionName', 'desc')"
                        :class="iconCls('mainRegionName')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('mainRegionName', 'asc')"
                        :class="iconCls('mainRegionName')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- Orders -->
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100"
                    style="width: 150px;"
                  >
                    <div class="flex items-center gap-1">
                      Ilość zamówień
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'ordersCount' && sort.type === 'asc'"
                        @click.prevent="setSort('ordersCount', 'desc')"
                        :class="iconCls('ordersCount')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('ordersCount', 'asc')"
                        :class="iconCls('ordersCount')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- Vehicle -->
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100"
                    style="width: 190px;"
                  >
                    <div class="flex items-center gap-1">
                      Pojazd
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'vehicleDisplay' && sort.type === 'asc'"
                        @click.prevent="setSort('vehicleDisplay', 'desc')"
                        :class="iconCls('vehicleDisplay')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('vehicleDisplay', 'asc')"
                        :class="iconCls('vehicleDisplay')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- Capacity -->
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100"
                    style="width: 150px;"
                  >
                    <div class="flex items-center gap-1">
                      Pojemność
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'capacitySort' && sort.type === 'asc'"
                        @click.prevent="setSort('capacitySort', 'desc')"
                        :class="iconCls('capacitySort')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('capacitySort', 'asc')"
                        :class="iconCls('capacitySort')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- Actions -->
                  <th
                    scope="col"
                    class="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    style="width: 72px;"
                  >
                    <span class="sr-only">Akcje</span>
                  </th>
                </tr>
              </thead>

              <tbody class="divide-y divide-slate-700 bg-slate-800 bg-opacity-40">
                <tr
                  v-for="row in mappedRoutes"
                  :key="row._id"
                  class="transition hover:bg-slate-700 hover:bg-opacity-30"
                >
                  <!-- ID -->
                  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-xs sm:pl-6">
                    <div class="flex items-center text-xs text-slate-100">
                      <span
                        v-tooltip="`${row._id}`"
                        @click.prevent="copyToClipboard(row._id)"
                        class="cursor-pointer font-semibold text-blue-300 transition hover:text-blue-200"
                      >
                        Kopiuj
                      </span>
                    </div>
                  </td>

                  <!-- Nazwa -->
                  <td class="px-3 py-4 text-xs text-slate-100 font-medium">
                    <div class="truncate" style="max-width: 180px;">
                      {{ row.name }}
                    </div>
                  </td>

                  <!-- Status -->
                  <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-100 font-medium">
                    {{ row.statusLabel }}
                  </td>

                  <!-- Region -->
                  <td class="px-3 py-4 text-xs text-slate-100 font-medium">
                    <div class="truncate" style="max-width: 180px;">
                      <span v-tooltip="row.regionTooltip">
                        {{ row.regionLabel }}
                      </span>
                    </div>
                  </td>

                  <!-- Orders -->
                  <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-100 font-medium">
                    {{ row.ordersCount }}
                  </td>

                  <!-- Vehicle -->
                  <td class="px-3 py-4 text-xs text-slate-100 font-medium">
                    <div class="truncate" style="max-width: 190px;">
                      {{ row.vehicleDisplay }}
                    </div>
                  </td>

                  <!-- Capacity -->
                  <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-100 font-medium">
                    <div class="flex items-center gap-2">
                      <span v-if="row.capacityIsLoading" class="capacity-spinner"></span>
                      <span>{{ row.capacityDisplay }}</span>
                    </div>
                  </td>

                  <!-- Actions -->
                  <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 select-none">
                    <div class="flex items-center justify-end gap-2">
                      <button
                        :v-tooltip="showArchive ? 'Podgląd' : 'Edycja'"
                        type="button"
                        @click.prevent="editRoute(row._id)"
                        class="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-80 transition hover:bg-slate-600"
                      >
                        <PencilSquareIcon class="h-4 w-4 text-slate-200" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <EmptyState
              v-if="routesList.length === 0 && !queryLoading"
              :title="'Brak tras do wyświetlenia'"
              :subtitle="showArchive
                ? 'Archiwum tras jest puste.'
                : 'Dodaj pierwszą trasę, aby zaplanować dostawy.'"
            />
          </div>
        </div>

        <div v-else class="flex items-center justify-center py-10">
          <div class="spinner"></div>
        </div>

        <div ref="infiniteSentinel" class="h-6"></div>

        <div
          class="mt-5 flex flex-col items-center justify-center gap-2"
          v-if="!queryFinished && routesList.length > 0"
        >
          <div class="text-xs text-slate-200" v-if="isLoadingMore">
            Ładowanie kolejnych tras…
          </div>

          <button
            type="button"
            @click.prevent="loadMore()"
            :disabled="isLoadingMore"
            class="inline-flex items-center rounded-2xl border border-slate-600 bg-slate-700 px-4 py-2.5 text-sm font-semibold text-slate-100 transition hover:bg-slate-600 disabled:opacity-50"
          >
            Załaduj więcej
          </button>
        </div>
      </div>
    </div>

    <!-- Modale do podpięcia później -->
    <!--
    <ModalNewRoute ... />
    <ModalManageRoute ... />
    <ModalPreviewRoute ... />
    -->
  </div>
</template>

<script>
import { HomeIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/solid'
import { PencilSquareIcon } from '@heroicons/vue/24/outline'
import EmptyState from '@/components/EmptyState.vue'
import { db } from '@/firebase/gfbconf.js'

export default {
  name: 'ManageRoutes',
  components: {
    EmptyState,
    HomeIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    PencilSquareIcon
  },
  data() {
    return {
      showArchive: false,
      pageSize: 25,
      lastVisible: null,
      queryLoading: false,
      isLoadingMore: false,
      queryFinished: false,
      routesList: [],
      pickedRoute: null,
      sort: {
        pickedColumn: 'status',
        type: 'asc'
      },
      capacityUsage: {},
      capacityLoading: {},
      capacityLoaderRunning: false,
      io: null
    }
  },

  created() {
    if (!this.$store.state.userData.permissions.admin) {
      this.$router.push('/hub')
      return
    }
  },

  mounted() {
    this.loadInitial()
    this.setupInfiniteObserver()
  },

  beforeUnmount() {
    if (this.io) {
      this.io.disconnect()
      this.io = null
    }
  },

  watch: {
    routesList() {
      this.loadCapacitiesSequential()
    }
  },

  computed: {
    mappedRoutes() {
      const nf = new Intl.NumberFormat('pl-PL')

      return this.routesList.map((r) => {
        const id = r.id || r._id || ''
        const status = Number(r.status ?? NaN)
        const statusLabel = this.statusLabel(status)

        const mainRegionName = r.mainRegion?.name || 'Nie przypisano'
        const allRegions = Array.isArray(r.allRegions) ? r.allRegions : []

        const allNames = allRegions
          .map((x) => x)
          .filter((x) => !!x)

        const otherNames = allNames.filter((n) => n !== mainRegionName)
        const extraCount = otherNames.length

        let regionLabel = mainRegionName
        if (extraCount > 0) regionLabel = `${mainRegionName} +${extraCount} więcej`

        const tooltipList = extraCount > 0 ? [mainRegionName, ...otherNames] : [mainRegionName]
        const regionTooltip = tooltipList.join(', ')

        const ordersCount = Array.isArray(r.orders) ? r.orders.length : 0

        const hasVehicle = !!r.vehicle
        const hasTrailer = !!r.trailer
        let vehicleDisplay = 'Nie wskazano'
        if (hasVehicle && hasTrailer) {
          const vehicleCapacity = Number(r.vehicle?.tankLitreCapacity ?? 0)
          vehicleDisplay = vehicleCapacity > 0 ? 'Solo + Przyczepa' : 'Cysterna'
        } else if (hasVehicle) {
          vehicleDisplay = 'Solo'
        }

        const capacity = Number(r.totalLitreCapacity ?? NaN)
        const y = Number.isFinite(capacity) ? nf.format(capacity) : '-'

        const used = this.capacityUsage[id]
        const isLoading = !!this.capacityLoading[id]

        let capacityDisplay = '-'
        if (Number.isFinite(capacity)) {
          if (isLoading || !Number.isFinite(Number(used))) {
            capacityDisplay = ` / ${y} L`
          } else {
            capacityDisplay = `${nf.format(used)} / ${y} L`
          }
        }

        return {
          _id: id,
          raw: r,
          name: r.name || '-',
          status,
          statusLabel,
          mainRegionName,
          regionLabel,
          regionTooltip,
          ordersCount,
          vehicleDisplay,
          capacityDisplay,
          capacitySort: Number.isFinite(capacity) ? capacity : -Infinity,
          capacityIsLoading: isLoading
        }
      })
    }
  },

  methods: {
    visit(path) {
      this.$router.push(path)
    },

    statusLabel(code) {
      switch (code) {
        case 1: return 'Zaplanowana'
        case 2: return 'W realizacji'
        case 3: return 'Zakończona'
        case 4: return 'Anulowana'
        default: return 'Nieznany status'
      }
    },

    sortFieldForColumn(col) {
      const map = {
        _id: 'plannedStart.unix',
        name: 'name',
        status: 'status',
        mainRegionName: 'mainRegion.name',
        capacitySort: 'totalLitreCapacity',
        ordersCount: 'ordersAmount',
        vehicleDisplay: 'vehicle'
      }
      return map[col] || null
    },

    async setSort(column, type) {
      const field = this.sortFieldForColumn(column)
      if (!field) {
        this.$store?.commit('setNotification', {
          show: true,
          head: 'Sortowanie niedostępne',
          subheader: 'Ta kolumna nie jest sortowalna po stronie Firestore (brak pola w dokumencie).',
          success: false
        })
        return
      }

      this.sort.pickedColumn = column
      this.sort.type = type
      await this.loadInitial()
    },

    iconCls(col) {
      return {
        'h-4 w-4 cursor-pointer transition hover:text-blue-300': true,
        'opacity-30': this.sort.pickedColumn !== col,
        'text-slate-300': true
      }
    },

    async changeList(type) {
      const targetArchive = type === 'archive'
      if (this.showArchive !== targetArchive) {
        this.showArchive = targetArchive
        this.capacityUsage = {}
        this.capacityLoading = {}
        this.capacityLoaderRunning = false
        await this.loadInitial()
      }
    },

    async loadInitial() {
      this.queryLoading = true
      this.isLoadingMore = false
      this.queryFinished = false
      this.lastVisible = null
      this.routesList = []

      try {
        const colRef = db.collection('Routes')
        const archiveValue = this.showArchive === true

        const field = this.sortFieldForColumn(this.sort.pickedColumn) || 'status'
        const dir = this.sort.type === 'desc' ? 'desc' : 'asc'
        const secondary = field === 'name' ? 'status' : 'name'

        const q = colRef
          .where('archive', '==', archiveValue)
          .orderBy(field, dir)
          .orderBy(secondary, 'asc')
          .limit(this.pageSize)

        const snap = await q.get()

        this.routesList = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        this.lastVisible = snap.docs[snap.docs.length - 1] || null

        if (snap.docs.length < this.pageSize) this.queryFinished = true
      } catch (e) {
        console.error('loadInitial routes error:', e)
        this.$store?.commit('setNotification', {
          show: true,
          head: 'Błąd pobierania tras',
          subheader: 'Sprawdź indeksy Firestore lub uprawnienia.',
          success: false
        })
        this.queryFinished = true
      } finally {
        this.queryLoading = false
      }
    },

    async loadMore() {
      if (this.queryFinished || this.isLoadingMore) return
      if (!this.lastVisible) return

      this.isLoadingMore = true

      try {
        const colRef = db.collection('Routes')
        const archiveValue = this.showArchive === true

        const field = this.sortFieldForColumn(this.sort.pickedColumn) || 'status'
        const dir = this.sort.type === 'desc' ? 'desc' : 'asc'
        const secondary = field === 'name' ? 'status' : 'name'

        const q = colRef
          .where('archive', '==', archiveValue)
          .orderBy(field, dir)
          .orderBy(secondary, 'asc')
          .startAfter(this.lastVisible)
          .limit(this.pageSize)

        const snap = await q.get()

        if (!snap.empty) {
          this.routesList.push(...snap.docs.map((d) => ({ id: d.id, ...d.data() })))
          this.lastVisible = snap.docs[snap.docs.length - 1] || this.lastVisible
        }

        if (snap.docs.length < this.pageSize) this.queryFinished = true
      } catch (e) {
        console.error('loadMore routes error:', e)
        this.queryFinished = true
      } finally {
        this.isLoadingMore = false
      }
    },

    setupInfiniteObserver() {
      const el = this.$refs.infiniteSentinel
      if (!el) return

      this.io = new IntersectionObserver(
        (entries) => {
          const first = entries[0]
          if (!first) return
          if (first.isIntersecting) {
            this.loadMore()
          }
        },
        {
          root: null,
          rootMargin: '300px',
          threshold: 0
        }
      )

      this.io.observe(el)
    },

    editRoute(id) {
      this.$router.push(`/dashboard/routes/details?id=${id}`)
    },

    copyToClipboard(text) {
      if (!text) return
      navigator.clipboard.writeText(text).then(() => {
        this.$store.commit('setNotification', {
          show: true,
          head: 'Skopiowano do schowka!',
          subheader: '',
          success: true
        })
      })
    },

    addRoute() {
      this.$store?.commit('setNotification', {
        show: true,
        head: 'Dodawanie trasy',
        subheader: 'Za chwilę podłączymy formularz dodawania/edycji trasy.',
        success: true
      })
    },

    async loadCapacitiesSequential() {
      if (this.capacityLoaderRunning) return
      this.capacityLoaderRunning = true

      try {
        for (const r of this.routesList) {
          const routeId = r.id || r._id
          if (!routeId) continue
          if (this.capacityUsage[routeId] != null) continue
          if (this.capacityLoading[routeId]) continue

          this.capacityLoading = { ...this.capacityLoading, [routeId]: true }

          const total = await this.loadCapacityForRoute(r)

          this.capacityUsage = { ...this.capacityUsage, [routeId]: total }
          this.capacityLoading = { ...this.capacityLoading, [routeId]: false }
        }
      } finally {
        this.capacityLoaderRunning = false
      }
    },

    async loadCapacityForRoute(route) {
      const orders = Array.isArray(route.orders) ? route.orders : []
      if (!orders.length) return 0

      let sum = 0

      for (const o of orders) {
        try {
          let snap = null

          if (o.ref && typeof o.ref.get === 'function') {
            snap = await o.ref.get()
          } else if (o.id) {
            snap = await db.collection('FuelOrders').doc(o.id).get()
          }

          if (snap && snap.exists) {
            const data = snap.data()
            const q = Number(data?.orderedFuel?.quantityLiters ?? NaN)
            if (Number.isFinite(q)) sum += q
          }
        } catch (e) {
          console.error('Błąd pobierania zamówienia dla trasy', route.id || route._id, e)
        }
      }

      return sum
    }
  }
}
</script>

<style lang="scss" scoped>
.spinner {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  border: 5px solid #475569;
  animation: spinner-bulqg1 1.3s infinite linear alternate, spinner-oaa3wk 2.6s infinite linear;
}

@keyframes spinner-bulqg1 {
  0% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 50% 0%, 50% 0%, 50% 0%, 50% 0%); }
  12.5% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 0%, 100% 0%, 100% 0%); }
  25% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 100%, 100% 100%, 100% 100%); }
  50% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%); }
  62.5% { clip-path: polygon(50% 50%, 100% 0, 100% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%); }
  75% { clip-path: polygon(50% 50%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 50% 100%, 0% 100%); }
  100% { clip-path: polygon(50% 50%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 0% 100%); }
}

@keyframes spinner-oaa3wk {
  0% { transform: scaleY(1) rotate(0deg); }
  49.99% { transform: scaleY(1) rotate(135deg); }
  50% { transform: scaleY(-1) rotate(0deg); }
  100% { transform: scaleY(-1) rotate(-135deg); }
}

.capacity-spinner {
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 9999px;
  border: 2px solid rgba(226, 232, 240, 0.6);
  border-top-color: transparent;
  animation: capacity-spin 0.6s linear infinite;
}

@keyframes capacity-spin {
  to { transform: rotate(360deg); }
}

.routesTable th,
.routesTable td {
  overflow: hidden;
}

.routesTable th > div,
.routesTable td > div {
  min-width: 0;
}
</style>